import { RoleFilterKey } from '@/Enums/UserManagement/RoleFilterKey.enum';
import { Grid } from '@/components/Grid';
import ServerSidePagination from '@/components/Grid/Pagination/ServerSidePagination';
import { EXPAND_OPTIONS } from '@/constants/grid';
import { ALL_CHECKED } from '@/constants/userManagement';
import { getUserRoleFilter } from '@/helpers/roles';
import { mapSortToApi } from '@/helpers/userManagement';
import { enterImpersonationMode } from '@/store/slices/authSlice';
import { setFilters, setSelectedUserId } from '@/store/slices/userManagementSlice';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import BulkPermissionModal from './Components/BulkPermissionModal';
import ToolBar from './Components/ToolBar';
import { getColumns } from './columns';

const UsersGrid = ({ data = [], meId, meRole, loading, rowCount, removeUser, handleOpenNewUserModal, filters }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // #region BULK PERMISSIONS
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkPermissionModalOpen, setBulkPermissionModalOpen] = useState(false);
  const handleCloseBulkPermissionModal = () => {
    setBulkPermissionModalOpen(false);
    setSelectedUserIds([]);
  };
  // Accumulated across every page seen, not just the current one — pagination
  // is server-side and `data` only ever holds the current page, but
  // `keepNonExistentRowsSelected` below lets a selection span several pages.
  // Deriving role from `data` at click time would silently lose it for any
  // selected user whose page is no longer loaded (CodeRabbit, flank#4504).
  const [roleByUserId, setRoleByUserId] = useState({});
  useEffect(() => {
    setRoleByUserId(prev => {
      const next = { ...prev };
      for (const row of data) next[row.id] = row.role?.value;
      return next;
    });
  }, [data]);
  // #endregion

  //#region sort and pagination
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 15,
  });

  const setPagination = useCallback(pagination => dispatch(setFilters({ ...pagination })), [dispatch]);
  const setSort = useCallback(sort => dispatch(setFilters({ sort })), [dispatch]);

  const handlePaginationModelChange = useCallback(
    newPaginationModel => {
      setPaginationModel(newPaginationModel);
      setPagination({ page: newPaginationModel.page + 1, limit: newPaginationModel.pageSize });
    },
    [setPagination],
  );

  const handleSortModelChange = newSortModel => {
    setSortModel(newSortModel);
    setSort(mapSortToApi(newSortModel));
  };

  useEffect(() => {
    setPaginationModel(oldModel => ({ ...oldModel, page: filters.page - 1 }));
  }, [filters.page]);
  //#endregion

  //#region columns
  const handleViewProfileClick = useCallback(
    selectedUserId => dispatch(setSelectedUserId({ selectedUserId })),
    [dispatch],
  );

  const handleEnterImpersonationMode = useCallback(
    async userId => {
      await dispatch(enterImpersonationMode({ userId }));
      navigate('/');
    },
    [dispatch, navigate],
  );

  const COLUMNS = useMemo(
    () => getColumns(handleEnterImpersonationMode, handleViewProfileClick, removeUser, meId, meRole),
    [handleEnterImpersonationMode, handleViewProfileClick, removeUser, meId, meRole],
  );
  //#endregion

  // #region USER ROLE FILTERS
  const setRoleFilter = userRoles => dispatch(setFilters({ userRoles }));

  const [userRoleFilter, setUserRoleFilter] = useState(ALL_CHECKED);

  const handleUserFilterChange = ({ target }) => {
    const { name, checked } = target;
    if (name === RoleFilterKey.ALL) {
      setRoleFilter(null);
      return setUserRoleFilter(ALL_CHECKED);
    }

    const keys = Object.values(RoleFilterKey)
      .slice(1)
      .filter(k => k !== name);
    if ((!checked && keys.every(k => !userRoleFilter[k])) || (checked && keys.every(k => userRoleFilter[k]))) {
      setRoleFilter(null);
      return setUserRoleFilter(ALL_CHECKED);
    }

    const updatedValue = {
      ...userRoleFilter,
      [RoleFilterKey.ALL]: false,
      [name]: checked,
    };
    setRoleFilter(getUserRoleFilter(updatedValue));
    return setUserRoleFilter(updatedValue);
  };
  // #endregion

  return (
    <div className='grid grid-cols-1'>
      <Grid
        rows={data}
        loading={loading}
        autosizeOnMount
        autosizeOptions={EXPAND_OPTIONS}
        checkboxSelection
        // A user can't bulk-apply permissions to themselves — that risks
        // locking themselves out of the screen they're editing from.
        isRowSelectable={({ id }) => id !== meId}
        keepNonExistentRowsSelected
        rowSelectionModel={selectedUserIds}
        onRowSelectionModelChange={setSelectedUserIds}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        columns={COLUMNS}
        columnHeaderHeight={30}
        sortingMode='server'
        paginationMode='server'
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 15 } },
          sorting: { sortModel },
        }}
        pagination
        slots={{
          pagination: ServerSidePagination,
          toolbar: ToolBar,
          columnSortedAscendingIcon: KeyboardArrowUp,
          columnSortedDescendingIcon: KeyboardArrowDown,
        }}
        slotProps={{
          pagination: {
            loading,
            rowCount,
          },
          footer: {
            className: 'relative',
          },
          toolbar: {
            usersTotal: rowCount,
            handleOpenNewUserModal,
            userRoleFilter,
            handleUserFilterChange,
            loading,
            selectedUserIds,
            onClearSelection: () => setSelectedUserIds([]),
            onApplyPermission: () => setBulkPermissionModalOpen(true),
          },
        }}
      />
      <BulkPermissionModal
        open={bulkPermissionModalOpen}
        handleClose={handleCloseBulkPermissionModal}
        userIds={selectedUserIds}
        roleByUserId={roleByUserId}
      />
    </div>
  );
};

UsersGrid.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  rowCount: PropTypes.number,
  removeUser: PropTypes.func,
  handleOpenNewUserModal: PropTypes.func,
  filters: PropTypes.object,
  meId: PropTypes.number,
  meRole: PropTypes.number,
};

export default UsersGrid;
